import {
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  checkRevertResellBuyback,
  revertResellBuyback,
} from 'lib/api/reSell/factoring';
import { ResellBuyback } from 'lib/api/reSell/factoring/types';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

interface RevertBuybackDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  buyback: ResellBuyback;
  setUp: () => Promise<void>;
}

const RevertBuybackDialog = (props: RevertBuybackDialogProps) => {
  const { open, setOpen, buyback, setUp } = props;
  const dispatch = useDispatch();

  const { data: canRevertData, isLoading } = checkRevertResellBuyback(
    dispatch,
    buyback?.id,
  );

  const { mutate: revertBuyback, isPending: isLoadingRevert } =
    revertResellBuyback(dispatch, buyback?.id);

  const handleRevertBuyback = async () => {
    revertBuyback(undefined, {
      onSuccess: () => {
        showAlert(dispatch, 'success', 'Recompra reversada correctamente');
        void setUp();
        setOpen(false);
      },
      onError: (error) => {
        errorHandler(dispatch, error);
      },
    });
  };

  const loading = isLoading || isLoadingRevert;

  return (
    <Dialog open={open}>
      <DialogTitle>
        Reversar recompra #{buyback.id} venta #{buyback.resellId} del fondo{' '}
        {buyback.fundName}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: '100px',
            }}
          >
            <CenteredCircularProgress type="dialog" />
          </div>
        ) : (
          <Stack spacing={2.5}>
            <Typography variant="body1" color="text.primary">
              ¿Estás seguro que quieres reversar esta recompra? Se ejecutarán
              las siguientes acciones:
            </Typography>

            {canRevertData?.warningMessages &&
              canRevertData.warningMessages.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{
                    borderRadius: 1,
                    '& .MuiAlert-message': {
                      width: '100%',
                    },
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                    Acciones que se ejecutarán
                  </AlertTitle>
                  <List
                    dense
                    sx={{
                      py: 0,
                      '& .MuiListItem-root': {
                        py: 0.5,
                        px: 0,
                      },
                    }}
                  >
                    {canRevertData.warningMessages.map(
                      (message: string, index: number) => (
                        <ListItem key={`${message}-${index}`} disableGutters>
                          <Typography
                            component="span"
                            sx={{
                              minWidth: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              fontSize: '1.2rem',
                              lineHeight: 1,
                            }}
                          >
                            •
                          </Typography>
                          <ListItemText
                            primary={message}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.primary',
                            }}
                          />
                        </ListItem>
                      ),
                    )}
                  </List>
                </Alert>
              )}

            {!canRevertData?.canRevert && canRevertData?.reason && (
              <Alert
                severity="error"
                icon={<ErrorIcon />}
                sx={{
                  borderRadius: 1,
                }}
              >
                <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                  No se puede reversar
                </AlertTitle>
                <Typography variant="body2" component="div">
                  {canRevertData.reason}
                </Typography>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="secondary"
          onClick={() => setOpen(false)}
          disabled={isLoadingRevert}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          color="error"
          onClick={handleRevertBuyback}
          disabled={!canRevertData?.canRevert || loading}
        >
          Reversar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default RevertBuybackDialog;
